import {
  BarrierLegParams,
  ObservationFrequency,
  PrimaryEventLegParams,
  ScheduleGenerator,
  ScheduleLeg,
  Strike,
} from '@/pages/instruments/new-instrument/schedule-builders/types'
import {
  _applyCouponSchedule,
  _getObservationEvents,
} from '@/pages/instruments/new-instrument/schedule-builders/utils'

export type AutocallConsolationOptions = {
  strikes: Strike[]
  currency_id: number
  initial_observation_date: Date
  maturity_date: Date
  settlement_cycle: number
  autocall_yearly_observations: ObservationFrequency
  coupon_rate_annual: number

  autocall_barrier_level: number
  payoff_barrier_level: number

  autocall_barrier_step: number | null

  knockdown_input_level: number | null
}

const buildPrimaryLeg = (
  params: PrimaryEventLegParams<AutocallConsolationOptions>,
): ScheduleLeg => {
  const {
    autocall_barrier_step,
    autocall_barrier_level,
    index,
    currency_id,
    strike,
    initial_observation_date,
    event,
  } = params

  const effectiveAutocallBarrierStep = autocall_barrier_step !== null ? autocall_barrier_step : 0
  const autocallBarrierLevel = autocall_barrier_level + effectiveAutocallBarrierStep * index

  return {
    leg_id: index + 1,
    option_type: 'Barrier Digital',
    barrier_type: 'Up-and-In',
    contract_type: 'Digital',
    region_type: 'European',
    currency_id: currency_id,
    strike: strike.value,
    strike_percent: strike.percent,
    coupon_rate: event.couponRate,
    instrument_id: strike.underlying_id,
    initial_observation_date: initial_observation_date,
    pay_date: event.payDate,
    start_observation_date: event.date,
    end_observation_date: event.date,
    participation_rate: 1,
    autocall_barrier_level: event.isAutocall ? autocallBarrierLevel : null,
    payoff_barrier_level: event.isCoupon ? autocallBarrierLevel : null,
  }
}

const buildBarrierLeg = (params: BarrierLegParams<AutocallConsolationOptions>): ScheduleLeg => {
  const { currency_id, strike, initial_observation_date, final_leg, knockdown_input_level } = params

  return {
    leg_id: final_leg.leg_id! + 2,
    option_type: 'Barrier Vanilla',
    barrier_type: 'Down-and-In',
    contract_type: 'Put',
    region_type: 'European',
    currency_id: currency_id,
    strike: strike.value,
    strike_percent: strike.percent,
    coupon_rate: null,
    instrument_id: strike.underlying_id,
    initial_observation_date: initial_observation_date,
    pay_date: final_leg.pay_date,
    start_observation_date: final_leg.start_observation_date,
    end_observation_date: final_leg.end_observation_date,
    participation_rate: -1,
    autocall_barrier_level: knockdown_input_level,
    payoff_barrier_level: knockdown_input_level,
  }
}

export const AutocallConsolationGenerator: ScheduleGenerator<AutocallConsolationOptions> = {
  generate({ strikes, ...params }) {
    const coupon_yearly_observations = params.autocall_yearly_observations

    const observationEvents = _getObservationEvents({ coupon_yearly_observations, ...params })

    const eventsWithCoupons = _applyCouponSchedule({
      events: observationEvents,
      accrue_coupon: true,
      coupon_yearly_observations,
      ...params,
    })

    const legs: ScheduleLeg[] = []

    for (const strike of strikes) {
      eventsWithCoupons.forEach((event, index) => {
        legs.push(buildPrimaryLeg({ strike, event, index, ...params }))
      })

      const final_leg = legs[legs.length - 1]

      // Consolation leg
      legs.push({
        ...final_leg,
        coupon_rate: final_leg.coupon_rate ? final_leg.coupon_rate / 2 : final_leg.coupon_rate,
        payoff_barrier_level: params.payoff_barrier_level,
        autocall_barrier_level: params.payoff_barrier_level,
        leg_id: final_leg.leg_id! + 1,
      })

      if (params.knockdown_input_level !== null) {
        legs.push(buildBarrierLeg({ strike, final_leg, ...params }))
      }
    }

    return legs
  },
}
